import { DateTime } from "luxon";
import { convertTimestampToLuxonOrDefaultNow } from "../utils/convertTimestampToLuxonOrDefaultNow";
export const createProgramFeatureUser = (data) => {
    if (!assertProgramFeatureUser(data, "save")) {
        throw new Error("ProgramFeatureUser.save failed");
    }
    return data;
};
export function assertProgramFeatureType(type) {
    const validTypes = [
        "raves",
        "liteRaves",
        "tasker",
        "incentives",
        "cyc",
    ];
    if (!validTypes.includes(type)) {
        throw new Error(`ProgramFeatureType must be one of: ${validTypes.join(", ")}`);
    }
}
export function assertProgramFeatureStatus(status) {
    const validStatuses = ["active", "inactive"];
    if (!validStatuses.includes(status)) {
        throw new Error(`ProgramFeatureStatus must be one of: ${validStatuses.join(", ")}`);
    }
}
export function assertProgramFeatureUserRoles(roles) {
    const validRoles = ["manager", "user"];
    if (roles.every((role) => !validRoles.includes(role))) {
        throw new Error(`ProgramFeatureUserRole must be one of: ${validRoles.join(", ")}`);
    }
}
export const assertProgramFeatureUser = (data, mode = "load") => {
    if (!data || typeof data !== "object") {
        throw new Error("ProgramFeatureUser must be an object");
    }
    const programFeatureUser = data;
    // Validate featureType field
    if (typeof programFeatureUser.featureType !== "string") {
        throw new Error("ProgramFeatureUser.featureType must be a string");
    }
    assertProgramFeatureType(programFeatureUser.featureType);
    // Required base fields based on mode
    const requiredFields = mode === "save"
        ? ["userId", "programId", "featureType", "status", "roles"]
        : ["id", "userId", "programId", "featureType", "status", "roles"];
    for (const field of requiredFields) {
        if (!(field in programFeatureUser)) {
            throw new Error(`ProgramFeatureUser is missing required field: ${field}`);
        }
    }
    // Type checks for base fields
    if (mode === "load" && typeof programFeatureUser.id !== "string")
        throw new Error("ProgramFeatureUser.id must be a string");
    if (typeof programFeatureUser.userId !== "string")
        throw new Error("ProgramFeatureUser.userId must be a string");
    if (typeof programFeatureUser.programId !== "string")
        throw new Error("ProgramFeatureUser.programId must be a string");
    if (typeof programFeatureUser.status !== "string")
        throw new Error("ProgramFeatureUser.status must be a string");
    if (!Array.isArray(programFeatureUser.roles))
        throw new Error("ProgramFeatureUser.roles must be an array");
    // Validate status and role
    assertProgramFeatureStatus(programFeatureUser.status);
    assertProgramFeatureUserRoles(programFeatureUser.roles);
    // Optional base field type checks
    if (programFeatureUser.metadata !== undefined &&
        programFeatureUser.metadata !== null &&
        typeof programFeatureUser.metadata !== "object")
        throw new Error("ProgramFeatureUser.metadata must be an object or null if provided");
    // Timestamp validation based on mode
    if (mode === "load") {
        if (programFeatureUser.createdAt !== undefined &&
            programFeatureUser.createdAt !== null &&
            !(programFeatureUser.createdAt instanceof DateTime)) {
            throw new Error("ProgramFeatureUser.createdAt must be a DateTime if present");
        }
        if (programFeatureUser.modifiedAt !== undefined &&
            programFeatureUser.modifiedAt !== null &&
            !(programFeatureUser.modifiedAt instanceof DateTime)) {
            throw new Error("ProgramFeatureUser.modifiedAt must be a DateTime if present");
        }
    }
    else {
        if (programFeatureUser.createdAt !== undefined) {
            const isDateTime = programFeatureUser.createdAt instanceof DateTime;
            const isPotentialFieldValue = typeof programFeatureUser.createdAt === "object" &&
                programFeatureUser.createdAt !== null &&
                !isDateTime;
            if (!isDateTime && !isPotentialFieldValue) {
                throw new Error("ProgramFeatureUser.createdAt must be a DateTime or FieldValue.serverTimestamp()");
            }
        }
        if (programFeatureUser.modifiedAt !== undefined) {
            const isDateTime = programFeatureUser.modifiedAt instanceof DateTime;
            const isPotentialFieldValue = typeof programFeatureUser.modifiedAt === "object" &&
                programFeatureUser.modifiedAt !== null &&
                !isDateTime;
            if (!isDateTime && !isPotentialFieldValue) {
                throw new Error("ProgramFeatureUser.modifiedAt must be a DateTime or FieldValue.serverTimestamp()");
            }
        }
    }
    return true;
};
export const createProgramFeatureUserConverter = (convertToFirestoreTimestampOrDefaultServerTime) => ({
    toFirestore(programFeatureUser) {
        if (!assertProgramFeatureUser(programFeatureUser, "save")) {
            throw new Error("ProgramFeatureUser.save failed");
        }
        const { id, ...programFeatureUserWithoutId } = programFeatureUser;
        const data = {
            ...programFeatureUserWithoutId,
            createdAt: convertToFirestoreTimestampOrDefaultServerTime(programFeatureUser.createdAt),
            modifiedAt: convertToFirestoreTimestampOrDefaultServerTime(programFeatureUser.modifiedAt),
        };
        return data;
    },
    fromFirestore(snapshot, options) {
        const data = snapshot.data(options);
        if (!data)
            throw new Error("Snapshot doesn't contain data");
        // Validate the featureType field
        if (typeof data.featureType !== "string") {
            throw new Error("Missing or invalid featureType field in Firestore document");
        }
        assertProgramFeatureType(data.featureType);
        // Create a base feature with common fields
        const baseProgramFeatureUser = {
            id: snapshot.id,
            userId: data.userId,
            programId: snapshot.ref.parent.parent?.parent?.parent?.id,
            featureType: data.featureType,
            status: data.status,
            roles: data.roles,
            createdAt: convertTimestampToLuxonOrDefaultNow(data.createdAt),
            modifiedAt: convertTimestampToLuxonOrDefaultNow(data.modifiedAt),
            metadata: data.metadata,
        };
        // Add optional base fields if they exist
        if (data.metadata !== undefined) {
            baseProgramFeatureUser.metadata = data.metadata;
        }
        // Build the specific programFeatureUser object based on the type
        let programFeatureUser;
        switch (data.featureType) {
            case "raves":
                programFeatureUser = {
                    ...baseProgramFeatureUser,
                };
                if (!assertProgramFeatureUser(programFeatureUser, "load")) {
                    throw new Error("ProgramFeatureUser.load failed");
                }
                break;
            case "liteRaves":
                programFeatureUser = {
                    ...baseProgramFeatureUser,
                };
                if (!assertProgramFeatureUser(programFeatureUser, "load")) {
                    throw new Error("ProgramFeatureUser.load failed");
                }
                break;
            case "tasker":
                programFeatureUser = {
                    ...baseProgramFeatureUser,
                };
                if (!assertProgramFeatureUser(programFeatureUser, "load")) {
                    throw new Error("ProgramFeatureUser.load failed");
                }
                break;
            case "cyc":
                programFeatureUser = {
                    ...baseProgramFeatureUser,
                };
                if (!assertProgramFeatureUser(programFeatureUser, "load")) {
                    throw new Error("ProgramFeatureUser.load failed");
                }
                break;
            case "incentives":
                programFeatureUser = {
                    ...baseProgramFeatureUser,
                };
                if (!assertProgramFeatureUser(programFeatureUser, "load")) {
                    throw new Error("ProgramFeatureUser.load failed");
                }
                break;
            default:
                throw new Error(`Unknown ProgramFeatureType: ${data.featureType}`);
        }
        return programFeatureUser;
    },
});
//# sourceMappingURL=ProgramFeatureUser.js.map